/**
 * Strips the server file/line suffix that PHP/Laravel appends to uncaught
 * exception messages before they reach a user-facing surface.
 *
 * When the API runs with debug output enabled, a thrown exception is serialized
 * as e.g.:
 *
 *   "Registration is closed for this competition. in /var/www/html/app/Services/Registrations/RegistrationWindowService.php:47"
 *
 * The trailing " in /path/to/File.php:47" is noise to the visitor and leaks
 * absolute server paths, so it is removed here and only the human-readable
 * sentence is kept.
 *
 * Note: this is a client-side mitigation, not a cure. The underlying fix is for
 * the API to stop returning debug-mode exception payloads on public endpoints.
 */

// Matches a trailing " in <path>:<line>" suffix. The path is intentionally
// matched on any extension (not just .php) so a future framework change on the
// backend does not silently reopen the leak. Anchored to the end of the string
// so prose containing the word "in" is never truncated.
const SERVER_TRACE_SUFFIX = /\s+in\s+\/\S+:\d+\s*$/

/**
 * Removes a trailing server file/line trace from a message.
 *
 * Returns non-string input untouched so callers can pipe values through it
 * without pre-checking, and preserves the original string when no trace is
 * present (including when stripping would leave nothing behind).
 */
export const stripServerTrace = <T>(value: T): T => {
  if (typeof value !== 'string') {
    return value
  }

  const stripped = value.replace(SERVER_TRACE_SUFFIX, '').trim()

  // A message that is *only* a trace has no human-readable part to show. Keep
  // the original rather than returning an empty string, so the caller's own
  // fallback logic (or the generic message) still has something to act on.
  return (stripped ? stripped : value) as unknown as T
}
